import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials } from '@/mocks/testimonials';

const teamMembers = [
  {
    name: '李明',
    role: '创始人 & CEO',
    desc: '十年社群公益经验，致力于为男同群体创造更包容的数字空间。',
    avatar:
      'https://readdy.ai/api/search-image?query=Professional%20Asian%20male%20portrait%20with%20warm%20confident%20expression%20on%20soft%20neutral%20gradient%20background%20clean%20modern%20headshot%20style&width=200&height=200&seq=team-01&orientation=squarish',
  },
  {
    name: '张伟',
    role: '内容总监',
    desc: '前媒体人，关注LGBTQ+议题多年，负责平台内容策略与质量把控。',
    avatar:
      'https://readdy.ai/api/search-image?query=Thoughtful%20Asian%20male%20portrait%20with%20gentle%20smile%20on%20soft%20warm%20gradient%20background%20clean%20modern%20headshot%20style&width=200&height=200&seq=team-02&orientation=squarish',
  },
  {
    name: '王浩',
    role: '社群运营',
    desc: '从社群中来，到社群中去。负责线上社群维护和线下活动策划执行。',
    avatar:
      'https://readdy.ai/api/search-image?query=Energetic%20Asian%20male%20portrait%20with%20bright%20friendly%20smile%20on%20soft%20coral%20gradient%20background%20clean%20modern%20headshot%20style&width=200&height=200&seq=team-03&orientation=squarish',
  },
];

const milestones = [
  { year: '2023', title: '平台创立', desc: '男同网正式上线，从零开始搭建男同群体的线上家园。' },
  { year: '2024', title: '内容爆发', desc: '文章数量突破100篇，覆盖六大核心板块，月活跃用户突破5000人。' },
  { year: '2025', title: '社群壮大', desc: '举办首场线下活动，社群成员突破10,000人，合作机构超过30家。' },
  { year: '2026', title: '持续成长', desc: '全面升级内容质量与用户体验，致力于成为最值得信赖的男同资讯平台。' },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('消息已发送！我们会尽快回复你。');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    fd.delete('company_alt');

    try {
      const resp = await fetch('https://readdy.ai/api/form/d9sn555lbrt1qcgaj6b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await resp.text();
      let parsed: Record<string, unknown> = { code: '' };
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (resp.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMsg('消息已发送！我们会尽快回复你。');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || text;
        setFormStatus('error');
        setFormMsg(typeof serverMsg === 'string' ? serverMsg : '发送失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="py-16 md:py-24 px-6 md:px-10 bg-background-100 border-b border-background-200/70">
          <div className="mx-auto text-center" style={{ maxWidth: '700px' }}>
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-[var(--font-heading)]">
              关于男同网
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              男同网创立于2023年，是专为男同群体打造的综合性资讯与社群平台。
              我们致力于为男同群体提供安全、包容、温暖的线上空间，
              让每一位社群成员都能在这里找到归属感。
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="mx-auto" style={{ maxWidth: '1000px' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '我们的使命',
                  desc: '为男同群体提供安全、可靠、全面的资讯与社交服务，帮助每一位成员找到属于自己的生活方式和社交圈。',
                },
                {
                  icon: 'ri-eye-line',
                  title: '我们的愿景',
                  desc: '成为中国最具影响力和包容性的男同社群平台，推动社会对男同群体的理解与接纳，让每个男同都能自信生活。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '我们的价值观',
                  desc: '尊重多元、守护隐私、倡导平等。我们相信每个人都有权利追求幸福，无论性取向如何。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 font-[var(--font-heading)]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="mx-auto" style={{ maxWidth: '800px' }}>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 text-center font-[var(--font-heading)] mb-12">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-px bg-background-300/60"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((m, idx) => (
                  <div key={m.year} className="flex items-start gap-6 pl-12 relative">
                    <div className="absolute left-2.5 top-1.5 w-5 h-5 rounded-full bg-primary-500 border-4 border-background-100"></div>
                    <div>
                      <span className="text-xs font-semibold text-primary-500 bg-primary-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                        {m.year}
                      </span>
                      <h3 className="mt-2 text-base font-semibold text-foreground-950 font-[var(--font-heading)]">
                        {m.title}
                      </h3>
                      <p className="mt-1 text-sm text-foreground-600">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="mx-auto" style={{ maxWidth: '1000px' }}>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 text-center font-[var(--font-heading)] mb-4">
              核心团队
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              一群热爱社群、志同道合的伙伴
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto"
                  />
                  <h3 className="mt-4 text-base font-semibold text-foreground-950 font-[var(--font-heading)]">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-500 font-medium mt-1">{member.role}</p>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="reputation" className="py-16 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="mx-auto" style={{ maxWidth: '1000px' }}>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 text-center font-[var(--font-heading)] mb-4">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              来自社群成员的真实声音
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.location}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{t.content}&rdquo;</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="mx-auto" style={{ maxWidth: '700px' }}>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 text-center font-[var(--font-heading)] mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              有任何问题、建议或合作意向？欢迎与我们取得联系。
            </p>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'hello@nantongwang.com' },
                { icon: 'ri-wechat-line', label: '微信公众号', value: '男同网' },
                { icon: 'ri-map-pin-line', label: '办公地址', value: '中国 · 上海' },
              ].map((info) => (
                <div
                  key={info.label}
                  className="p-4 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${info.icon} text-lg text-primary-500`}></i>
                  </div>
                  <p className="text-xs text-foreground-500">{info.label}</p>
                  <p className="text-sm font-medium text-foreground-950 mt-1">{info.value}</p>
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 bg-background-100 rounded-xl border border-background-200/70 p-6"
              data-readdy-form="true"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none -z-10"
              />
              <div>
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你想说的话（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
              </div>
              <button
                type="submit"
                className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors w-full sm:w-auto sm:self-start"
              >
                发送消息
              </button>
              {formStatus !== 'idle' && (
                <p className={`text-sm ${formStatus === 'success' ? 'text-primary-600' : 'text-accent-600'}`}>
                  {formMsg}
                </p>
              )}
            </form>
          </div>
        </section>

        {/* Schema.org */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于男同网',
              description: '了解男同网的品牌故事、发展历程和核心团队。',
              url: 'https://nantongwang.com/about',
              isPartOf: {
                '@type': 'WebSite',
                name: '男同网',
                url: 'https://nantongwang.com',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://nantongwang.com/' },
                  { '@type': 'ListItem', position: 2, name: '关于我们' },
                ],
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}